import React, { useState } from 'react';
import { Check, Lock, Minus, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import { Action, Modal } from '../aa';
import { billingApi, type SeatSummary } from '../../services/billingApi';

const dollars = (cents: number) => `$${(cents / 100).toLocaleString(undefined, {
  minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  maximumFractionDigits: 2,
})}`;

const longDay = (date: Date) => date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const errorDetail = (err: unknown): string | undefined => {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (typeof detail !== 'string') return undefined;
  return detail;
};

/* The share of the current period still to run, for a prorated estimate.
 * Stripe works the exact figure; this only sets expectations. */
const periodLeft = (renewsAt: string | null | undefined): number => {
  if (!renewsAt) return 1;
  const end = new Date(renewsAt);
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  const left = (end.getTime() - Date.now()) / (end.getTime() - start.getTime());
  return Math.min(Math.max(left, 0), 1);
};

const SummaryRow: React.FC<{ label: React.ReactNode; sub?: React.ReactNode; value: React.ReactNode; strong?: boolean }> = ({
  label, sub, value, strong = false,
}) => (
  <div className="flex items-start justify-between gap-4 py-2.5">
    <div className="min-w-0">
      <div className={`text-[13.5px] ${strong ? 'font-semibold text-ink' : 'text-ink'}`}>{label}</div>
      {sub && <div className="mt-0.5 text-[12px] leading-[19px] text-ink-dim">{sub}</div>}
    </div>
    <div className={`shrink-0 text-right tabular-nums ${strong ? 'text-[16px] font-semibold text-ink' : 'text-[13.5px] text-ink'}`}>{value}</div>
  </div>
);

const StepButton: React.FC<{ onClick: () => void; disabled: boolean; label: string; children: React.ReactNode }> = ({
  onClick, disabled, label, children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="grid h-11 w-11 place-items-center rounded-full border border-line-strong bg-white text-ink transition-colors hover:bg-canvas-inset disabled:cursor-not-allowed disabled:border-line disabled:text-ink-soft disabled:hover:bg-white"
  >
    {children}
  </button>
);

/* Stripe's checkout sends the Owner back through /settings, whose redirect
 * drops query params, so the session id waits here. Billing confirms it on
 * return, so the Team plan starts even when the webhook is late or missed. */
const PENDING_SEAT_CHECKOUT = 'aa_pending_seat_checkout';

const writePendingSeatCheckout = (sessionId: string | null) => {
  try {
    if (sessionId) {
      sessionStorage.setItem(PENDING_SEAT_CHECKOUT, sessionId);
      return;
    }
    sessionStorage.removeItem(PENDING_SEAT_CHECKOUT);
  } catch {
    /* storage blocked: the webhook still starts the plan */
  }
};

const readPendingSeatCheckout = (): string | null => {
  try {
    return sessionStorage.getItem(PENDING_SEAT_CHECKOUT);
  } catch {
    return null;
  }
};

/* Runs before Billing loads, so the page opens on the Team plan. */
export const finishSeatCheckout = async (): Promise<void> => {
  const pending = readPendingSeatCheckout();
  if (!pending) return;
  writePendingSeatCheckout(null);
  try {
    const { data } = await billingApi.confirmSeatPlan(pending);
    if (data.activated) toast.success('Your Team plan is active.');
  } catch {
    /* the webhook starts the plan; Billing shows whatever is there */
  }
};

export interface SeatPlanModalProps {
  summary: SeatSummary;
  restartSeats?: number;
  onClose: () => void;
  onChanged: (summary: SeatSummary) => void;
}

/* One place to pick seats. Both ways it counts seats to ADD: moving from
 * Free to the Team plan adds them on top of everyone who has already joined
 * (they always take a seat; Stripe takes the payment on its own page), and
 * the stepper shows the total, the Owner's own seat included. On the Team
 * plan it adds to the seats the plan has, charged now to the card. */
export const SeatPlanModal: React.FC<SeatPlanModalProps> = ({ summary, restartSeats, onClose, onChanged }) => {
  const adding = summary.plan === 'workspace';
  const restarting = !adding && !!summary.ended_plan;
  const joined = Math.max(summary.joined ?? 1, 1);
  const pending = summary.pending_invites ?? 0;
  const max = summary.max_seats ?? 500;
  const price = summary.seat_price_cents ?? 0;
  const allowance = summary.allowance_per_seat_cents ?? 0;
  const current = summary.seats ?? joined;

  const base = adding ? current : joined;
  const floor = adding ? 1 : 0;
  const ceiling = max - base;
  const full = ceiling < floor;
  // Starting the plan, the stepper shows the total seats; adding, the seats to add.
  const offset = adding ? 0 : base;

  const [count, setCount] = useState(() => {
    let initial = 1;
    if (restartSeats) initial = restartSeats - joined;
    return Math.min(Math.max(initial, floor), Math.max(ceiling, floor));
  });
  const [draft, setDraft] = useState(String(count + offset));
  const [busy, setBusy] = useState(false);

  const choose = (next: number) => {
    const clamped = Math.min(Math.max(Math.round(next), floor), Math.max(ceiling, floor));
    setCount(clamped);
    setDraft(String(clamped + offset));
  };

  const onType = (value: string) => {
    setDraft(value.replace(/[^0-9]/g, ''));
    const n = parseInt(value, 10) - offset;
    if (!Number.isNaN(n) && n >= floor && n <= ceiling) setCount(n);
  };

  const seats = base + count;
  const monthly = seats * price;
  const pooled = seats * allowance;
  const prorated = Math.round(count * price * periodLeft(summary.renews_at));
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);

  const start = async () => {
    setBusy(true);
    const back = `${window.location.origin}/settings?tab=payment`;
    try {
      const { data } = await billingApi.startSeatPlan(seats, back, back);
      writePendingSeatCheckout(data.session_id);
      window.location.assign(data.url);
    } catch (err) {
      toast.error(errorDetail(err) || 'Could not open checkout. Try again.');
      setBusy(false);
    }
  };

  const change = async () => {
    setBusy(true);
    try {
      const { data } = await billingApi.changeSeats(seats);
      toast.success(`${plural(count, 'seat', 'seats')} added. Your plan now has ${plural(data.seats ?? seats, 'seat', 'seats')}.`);
      onChanged(data);
      onClose();
    } catch (err) {
      toast.error(errorDetail(err) || 'Could not change seats. Try again.');
      setBusy(false);
    }
  };

  let stepLabel = 'Seats';
  let seatNote = '1 seat is yours as the Owner.';
  if (joined > 1) seatNote = `${joined} seats are taken: yours as the Owner and ${plural(joined - 1, 'person', 'people')} who joined.`;
  if (count > 0) seatNote = `${seatNote} ${count} more for people you invite.`;
  if (pending > 0) seatNote = `${seatNote} ${plural(pending, 'invite is', 'invites are')} waiting.`;
  let minNote = '';
  if (!adding && count === ceiling) minNote = `${max} seats is the most you can buy here. Contact us for more.`;
  if (adding) {
    stepLabel = 'Seats to add';
    seatNote = `You have ${plural(current, 'seat', 'seats')} now: ${joined} in use, ${Math.max(current - joined, 0)} open. After this: ${plural(seats, 'seat', 'seats')}.`;
  }
  if (adding && !full && count === ceiling) minNote = `${max} seats is the most a plan can have here. Contact us for more.`;
  if (full) minNote = `Your plan already has ${max} seats, the most it can have here. Contact us for more.`;

  let todayLabel = 'Due today';
  let todayValue = dollars(monthly);
  let todaySub: React.ReactNode = `Then ${dollars(monthly)} every month, renewing on ${longDay(nextMonth)}.`;
  if (adding) {
    todayLabel = 'Charged today';
    todayValue = `about ${dollars(prorated)}`;
    todaySub = `For ${plural(count, 'added seat', 'added seats')} until your renewal on ${summary.renews_at ? longDay(new Date(summary.renews_at)) : 'your next billing date'}, then ${dollars(monthly)} a month. The exact amount is on the receipt.`;
  }

  let primaryLabel = `Continue to payment · ${dollars(monthly)}`;
  let primary = start;
  if (adding) {
    primaryLabel = `Add ${plural(count, 'seat', 'seats')}`;
    primary = change;
  }

  let modalContext = 'Move to the Team plan';
  if (restarting) modalContext = 'Restart the Team plan';
  if (adding) modalContext = 'Team plan';

  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-[12px] text-ink-dim">
        <Lock size={13} /> {adding ? 'Charged to the card on your plan' : "Paid on Stripe's secure page"}
      </span>
      <span className="flex gap-2">
        <Action variant="quiet" onClick={onClose} disabled={busy} reason="Working">Cancel</Action>
        <Action onClick={primary} disabled={busy || full} reason={busy ? 'Working' : 'Your plan already has the most seats it can have here.'}>
          {primaryLabel}
        </Action>
      </span>
    </div>
  );

  return (
    <Modal size="dialog" onClose={onClose} context={modalContext} footer={footer}>
      <h2 className="m-0 text-[22px] font-semibold leading-[30px] text-ink">
        {adding ? 'Add seats' : 'Choose your seats'}
      </h2>
      <p className="m-0 mt-1 text-[13.5px] leading-[22px] text-ink-dim">
        {adding
          ? `Make room for more people before they join. ${dollars(price)} a month per seat.`
          : `Every seat is ${dollars(price)} a month, your own included. Pick how many seats your team needs, and change it any time.`}
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-panel border border-line px-5 py-4">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-ink">{stepLabel}</div>
          <div className="mt-0.5 text-[12px] text-ink-dim">{seatNote}</div>
        </div>
        <div className="flex items-center gap-3">
          <StepButton onClick={() => choose(count - 1)} disabled={busy || full || count <= floor} label="One seat fewer">
            <Minus size={16} />
          </StepButton>
          <input
            aria-label={adding ? 'Number of seats to add' : 'Number of seats'}
            inputMode="numeric"
            value={draft}
            onChange={(e) => onType(e.target.value)}
            onBlur={() => choose((parseInt(draft, 10) || floor + offset) - offset)}
            disabled={busy || full}
            className="h-11 w-16 rounded-act border border-line-strong bg-white text-center text-[18px] font-semibold tabular-nums text-ink outline-none focus:border-ink focus:ring-[3px] focus:ring-ink/10"
          />
          <StepButton onClick={() => choose(count + 1)} disabled={busy || full || count >= ceiling} label="One seat more">
            <Plus size={16} />
          </StepButton>
        </div>
      </div>
      {minNote && <p className="m-0 mt-2 text-[12px] text-ink-dim">{minNote}</p>}

      <div className="mt-5 rounded-panel bg-canvas-inset px-5 py-2">
        <SummaryRow label="Team plan" sub={`${dollars(price)} × ${plural(seats, 'seat', 'seats')}`} value={`${dollars(monthly)} / month`} />
        {allowance > 0 && (
          <SummaryRow
            label="Included agent usage"
            sub={`${dollars(allowance)} × ${plural(seats, 'seat', 'seats')}, shared by the whole team`}
            value={<span className="text-[#3F7A2F]">{dollars(pooled)} / month included</span>}
          />
        )}
        <div className="border-t border-line" />
        <SummaryRow strong label={todayLabel} sub={todaySub} value={todayValue} />
      </div>

      <ul className="m-0 mt-5 list-none space-y-2.5 p-0">
        {[
          'Invite everyone on your team. A person takes a seat when they accept.',
          allowance > 0 ? `${dollars(allowance)} of agent usage per seat each month, pooled for the whole company.` : 'Agent usage for the whole company, pooled.',
          'Your prepaid balance carries over from month to month.',
          'If more people join than you have seats, a seat is added for the rest of that month.',
        ].map((line) => (
          <li key={line} className="flex items-start gap-2.5 text-[13px] leading-[21px] text-ink">
            <span className="mt-[2px] grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full bg-ok-soft text-ok-dim">
              <Check size={12} strokeWidth={3} />
            </span>
            {line}
          </li>
        ))}
      </ul>
    </Modal>
  );
};

export default SeatPlanModal;
